import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import type { FormValues, Client, Assessment } from '@/lib/types';
import { getClient, updateClient } from '@/components/clients/services/clients-api';
import {
  createAssessment,
  updateAssessment,
} from '@/components/sop-intake/services/assessments-api';
import { exportSopAnalysisToPdf } from '@/lib/pdf-export';
import logger from '@/lib/logging';
import { postAnalyzeSop } from '@/lib/evaluation-api-client';
import { mergeAssessmentScalarsIntoTemplate } from '@/lib/sanitize-sop-form-values-for-api';
import type { SopAnalysisResult } from '@/components/sop-intake/sop-analyzer/types';
import { useSettings } from '@/components/settings/lib/settings-context';
import type { RuleDocVersion } from '@/lib/posture-analysis/rule-params';
import { invalidateParamsCache } from '@/lib/posture-analysis/rule-params';
import { clientDisplayLabel } from '@/lib/clients/roster-fields';

const demoValues: FormValues = {
  L_CervicalAxialRotation: 25,
  R_CervicalAxialRotation: 35,
  L_CervicalLateralFlexion: 28,
  R_CervicalLateralFlexion: 25,
  L_ShoulderIR: 80,
  R_ShoulderIR: 70,
  L_ShoulderER: 95,
  R_ShoulderER: 85,
  L_ShoulderFlexion: 140,
  R_ShoulderFlexion: 155,
  L_ShoulderAbduction: 55,
  R_ShoulderAbduction: 50,
  L_PelvicRotation: 65,
  R_PelvicRotation: 75,
  L_HipExtension: 5,
  R_HipExtension: -5,
  L_HipAbduction: 40,
  R_HipAbduction: 35,
  L_StraightLegRaise: 85,
  R_StraightLegRaise: 75,
  L_FemoralIR: 35,
  R_FemoralIR: 25,
  L_FemoralER: 50,
  R_FemoralER: 40,
  FootPresentation: '[ L ] > [ R ]',
  GroundingMode: 'dynamometer',
  L_GroundingPerformance_actual: null,
  R_GroundingPerformance_actual: null,
  L_GroundingPerformance_perceived: null,
  R_GroundingPerformance_perceived: null,
  // Backward compatibility with legacy single-score grounding fields.
  L_GroundingPerformance: null,
  R_GroundingPerformance: null,
  testMode: 'passive',
  HipExtension_procedureVariant: 'standing',
};

const emptyValues: FormValues = Object.keys(demoValues).reduce((acc, key) => {
  acc[key] = '';
  return acc;
}, {} as FormValues);

/** Fields that describe how the form is filled, not a measured degree. */
const SOP_FORM_METADATA_KEYS = new Set([
  'testMode',
  'GroundingMode',
  'HipExtension_procedureVariant',
]);

/** A new assessment starts with no degrees and no phase. Metadata stays so the form still works. */
function blankSopFormValues(): FormValues {
  return normalizeGroundingFields({
    ...emptyValues,
    testMode: 'passive',
    GroundingMode: 'dynamometer',
    HipExtension_procedureVariant: 'standing',
  });
}

function hasSopMeasurements(values: FormValues): boolean {
  return Object.entries(values).some(
    ([key, value]) => !SOP_FORM_METADATA_KEYS.has(key) && !isBlankValue(value),
  );
}

const normalizeGroundingFields = (values: FormValues): FormValues => {
  const next: FormValues = { ...values };
  const mode = typeof next.GroundingMode === 'string' ? next.GroundingMode.toLowerCase() : '';
  next.GroundingMode = mode === 'perceived' ? 'perceived' : 'dynamometer';
  (['L', 'R'] as const).forEach((side) => {
    const actualKey = `${side}_GroundingPerformance_actual` as keyof FormValues;
    const perceivedKey = `${side}_GroundingPerformance_perceived` as keyof FormValues;
    const legacyKey = `${side}_GroundingPerformance` as keyof FormValues;

    const actualVal = next[actualKey];
    const perceivedVal = next[perceivedKey];
    const legacyVal = next[legacyKey];

    // If legacy assessments only saved one grounding score, surface it in the new "actual" field.
    if (isBlankValue(actualVal) && !isBlankValue(legacyVal)) {
      next[actualKey] = legacyVal;
    }
    // If only new values exist, mirror actual to legacy so old readers still get a value.
    if (isBlankValue(legacyVal) && !isBlankValue(actualVal)) {
      next[legacyKey] = actualVal;
    }
    // Keep perceived available but don't force-write it into legacy.
    if (isBlankValue(next[actualKey]) && !isBlankValue(perceivedVal) && isBlankValue(next[legacyKey])) {
      next[legacyKey] = perceivedVal;
    }
  });

  return next;
};

const isBlankValue = (value: unknown): boolean =>
  value === null || value === undefined || value === '' || value === 'Select...';

export function sopAnalyzeFailureMessage(error: unknown): string {
  const m = error instanceof Error ? error.message : String(error);
  if (/HTTP\s*401|Unauthorized|not authenticated|not logged in/i.test(m)) {
    return 'Sign in required to run analysis. Refresh the page or sign in again.';
  }
  if (/invalid csrf token|csrf/i.test(m)) {
    return 'Session security token expired. Refresh the page and try again.';
  }
  if (/HTTP\s*403|Forbidden|permission/i.test(m)) {
    return 'You do not have permission to run analysis. Check your account role.';
  }
  if (/HTTP\s*400|Invalid request/i.test(m)) {
    return 'Analysis request was rejected. Check inputs or try again.';
  }
  if (/HTTP\s*404/i.test(m)) {
    return 'Analysis service is unavailable. Try again later or contact support.';
  }
  return 'Analysis failed. Please review your inputs and try again.';
}

export interface UseSopAnalyzerOptions {
  /**
   * Default true: after the first save of a new assessment, navigate to its page.
   * Remote Assessment passes false to stay on the workbench; later saves then update the
   * assessment created by the first save instead of creating another.
   */
  navigateAfterCreate?: boolean;
}

export function useSopAnalyzer(
  client: Client | null,
  setClient: (client: Client | null) => void,
  initialData?: Assessment | null,
  options: UseSopAnalyzerOptions = {},
) {
  const navigateAfterCreate = options.navigateAfterCreate ?? true;
  /** Id of the assessment this hook created when it did not navigate away (stay-on-page saves). */
  const createdAssessmentIdRef = useRef<string | null>(null);
  /** Same id as state, so the UI can switch its wording (Save -> Update) once it exists. */
  const [createdAssessmentId, setCreatedAssessmentId] = useState<string | null>(null);
  const [formValues, setFormValues] = useState<FormValues>(
    normalizeGroundingFields(
      initialData
        ? mergeAssessmentScalarsIntoTemplate(emptyValues, initialData as Record<string, unknown>)
        : blankSopFormValues()
    )
  );
  const [analysis, setAnalysis] = useState<SopAnalysisResult | null>(null);
  const [lastAnalysis, setLastAnalysis] = useState<SopAnalysisResult | null>(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysisError, setAnalysisError] = useState<string | null>(null);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved'>('idle');

  const { isInitialized, serverUser } = useAuth();
  const { settings } = useSettings();
  const { toast } = useToast();
  const navigate = useNavigate();
  const version: RuleDocVersion = settings.useLegacySopAssessment ? 'legacy' : 'current';
  const postureClassification = client?.postureAnalyses?.[0]?.classification?.pattern;

  useEffect(() => {
    if (initialData) {
      setFormValues(
        normalizeGroundingFields(mergeAssessmentScalarsIntoTemplate(emptyValues, initialData as Record<string, unknown>))
      );
    }
  }, [initialData]);

  useEffect(() => {
    invalidateParamsCache();
  }, [version]);

  useEffect(() => {
    let cancelled = false;
    const ac = new AbortController();
    if (!isInitialized || !serverUser) {
      setAnalysis(null);
      setIsAnalyzing(false);
      return;
    }
    const hasValues = hasSopMeasurements(formValues);
    if (!hasValues) {
      setAnalysis(null);
      setIsAnalyzing(false);
      return;
    }

    const debounceMs = 280;
    const tid = setTimeout(() => {
      setIsAnalyzing(true);
      postAnalyzeSop(
        {
          formValues,
          ruleDocVersion: version,
          postureClassification: postureClassification ?? null
        },
        { signal: ac.signal }
      )
        .then((compiled) => {
          if (!cancelled) {
            setAnalysis(compiled);
            setLastAnalysis(compiled);
            setAnalysisError(null);
          }
        })
        .catch((error: unknown) => {
          if (cancelled || (error as Error)?.name === 'AbortError') return;
          logger.error('Failed to run SoP analysis', error);
          setAnalysisError(sopAnalyzeFailureMessage(error));
          setAnalysis(null);
        })
        .finally(() => {
          if (!cancelled) {
            setIsAnalyzing(false);
          }
        });
    }, debounceMs);

    return () => {
      cancelled = true;
      ac.abort();
      clearTimeout(tid);
    };
  }, [formValues, version, postureClassification, isInitialized, serverUser]);

  const handleInputChange = useCallback((id: string, value: string) => {
    setFormValues((prev) => {
      const next = { ...prev, [id]: value } as FormValues;
      if (id === 'GroundingMode') {
        next.GroundingMode = value === 'perceived' ? 'perceived' : 'dynamometer';
      }
      if (id.endsWith('_GroundingPerformance_actual')) {
        const side = id.startsWith('L_') ? 'L' : 'R';
        next[`${side}_GroundingPerformance` as keyof FormValues] = value;
      }
      if (id.endsWith('_GroundingPerformance') && !id.endsWith('_actual') && !id.endsWith('_perceived')) {
        const side = id.startsWith('L_') ? 'L' : 'R';
        if (isBlankValue(next[`${side}_GroundingPerformance_actual` as keyof FormValues])) {
          next[`${side}_GroundingPerformance_actual` as keyof FormValues] = value;
        }
      }
      return normalizeGroundingFields(next);
    });
  }, []);

  const resetForm = useCallback(() => {
    createdAssessmentIdRef.current = null;
    setCreatedAssessmentId(null);
    setFormValues(blankSopFormValues());
    setAnalysis(null);
    setLastAnalysis(null);
    setSaveStatus('idle');
  }, []);

  const persistClassification = async (clientId: string, classification: string) => {
    if (!serverUser) return;
    await updateClient(clientId, { classification });
  };

  const handleSave = useCallback(async () => {
    if (!client) {
      toast({
        title: 'Cannot Save',
        description: 'Select a client to save this assessment.',
        variant: 'destructive',
      });
      return;
    }

    if (!analysis) {
      const hasAnyValue = hasSopMeasurements(formValues);
      toast({
        title: 'Cannot Save',
        description: analysisError
          ? 'Fix the analysis error above, then try Save again.'
          : hasAnyValue
            ? 'Wait for analysis to finish or fix the error shown above.'
            : 'Complete the assessment before saving.',
        variant: 'destructive',
      });
      return;
    }

    if (!serverUser) {
      toast({
        title: 'Authentication Error',
        description: 'Please sign in before saving.',
        variant: 'destructive',
      });
      return;
    }

    setSaveStatus('saving');
    try {
      const targetClient = await getClient(client.id);
      if (!targetClient) {
        toast({
          title: 'Client Not Found',
          description: 'The selected client no longer exists.',
          variant: 'destructive',
        });
        setClient(null);
        setSaveStatus('idle');
        return;
      }

      const payload = {
        ...formValues,
        type: 'SOP',
        updatedAt: new Date(),
      };

      let newAssessmentId: string | undefined;
      const existingId = initialData?.id ?? createdAssessmentIdRef.current;
      if (existingId) {
        await updateAssessment(client.id, existingId, payload);
      } else {
        newAssessmentId = await createAssessment(client.id, payload as Omit<Assessment, 'id'>);
        if (!navigateAfterCreate && newAssessmentId) {
          createdAssessmentIdRef.current = newAssessmentId;
          setCreatedAssessmentId(newAssessmentId);
        }
      }

      await persistClassification(client.id, analysis.classificationCode);

      toast({
        title: existingId ? 'Assessment Updated' : 'Assessment Saved',
        description: `${clientDisplayLabel(client)}'s SOP assessment is up to date.`,
      });

      setSaveStatus('saved');
      setTimeout(() => {
        setSaveStatus('idle');
        if (newAssessmentId && navigateAfterCreate) {
          navigate(`/clients/${client.id}/assessment/${encodeURIComponent(newAssessmentId)}`);
        }
      }, 1500);
    } catch (error) {
      logger.error('Failed to save assessment', error);
      toast({
        title: 'Save Error',
        description: 'Something went wrong while saving the assessment.',
        variant: 'destructive',
      });
      setSaveStatus('idle');
    }
  }, [client, analysis, analysisError, formValues, initialData, navigate, navigateAfterCreate, serverUser, setClient, toast]);

  const handleExportPdf = useCallback(async () => {
    if (!analysis) {
      toast({
        title: 'Nothing to Export',
        description: 'Complete the assessment to generate a PDF.',
        variant: 'destructive',
      });
      return;
    }

    try {
      await exportSopAnalysisToPdf(
        { id: initialData?.id || 'new', date: initialData?.date || new Date(), ...formValues } as Assessment,
        client,
        version
      );
    } catch (error) {
      logger.error('Failed to export SOP PDF', error);
      toast({
        title: 'Export Failed',
        description: 'Unable to generate the SOP PDF at this time.',
        variant: 'destructive',
      });
    }
  }, [analysis, client, formValues, initialData, toast, version]);

  return {
    /** True once this assessment exists (opened for edit, or created by a stay-on-page save). */
    isPersisted: Boolean(initialData?.id || createdAssessmentId),
    formValues,
    setFormValues,
    handleInputChange,
    analysis,
    lastAnalysis,
    isAnalyzing,
    analysisError,
    resetForm,
    handleSave,
    handleExportPdf,
    saveStatus,
  };
}
